import { useEffect } from 'react'

import { useAssociateDevice } from '../hooks/useAssociateDevice'
import { useForm } from 'react-hook-form'

import { Box, GridItem } from '@chakra-ui/react'

import {
  Button,
  FormInputSelectControl,
  SkeletonModalBody,
} from '@/components/ui'

import { EditDeviceFieldsI } from '../types/types'
import { AssociatedDevice } from './AssociatedDevice'
import { EditDeviceModalSection } from './EditDeviceModalSection'
import { SectionHeader } from './SectionHeader'
import { TwoColumnGrid } from './TwoColumnGrid'

interface AssociateDevicesIProps {
  deviceData: EditDeviceFieldsI
  isOtherSectionLoading: boolean
}

export interface AssociatedDevicesI {
  associateDevice: {
    label: string
    value: string
  }
}

export const AssociateDevices = ({
  deviceData,
  isOtherSectionLoading,
}: AssociateDevicesIProps) => {
  const deviceType = deviceData?.type
  const { control, watch, reset } = useForm<AssociatedDevicesI>({
    mode: 'onChange',
  })
  const {
    associatedDeviceOptions,
    selectedDevices,
    addSelectedDeviceOption,
    handleAssociation,
    removeSelectedDeviceOption,
    isDirty,
    isLoading,
  } = useAssociateDevice({
    deviceData,
  })
  const watchAssociateDevice = watch('associateDevice')

  useEffect(() => {
    if (watchAssociateDevice) {
      addSelectedDeviceOption(watchAssociateDevice)
      reset({ associateDevice: null })
    }
  }, [watchAssociateDevice])

  return (
    <Box>
      {isOtherSectionLoading ? (
        <SkeletonModalBody />
      ) : (
        <EditDeviceModalSection>
          <TwoColumnGrid>
            <GridItem>
              <SectionHeader
                subTitle={`Associate devices with this ${deviceType?.toLowerCase()} to include clips in related incidents.`}
                title='Associated Devices'
              />
            </GridItem>
            <GridItem justifySelf='end'>
              <Button
                isDisabled={!isDirty}
                isLoading={isLoading}
                loadingText='Saving Changes'
                onClick={handleAssociation}
              >
                Save
              </Button>
            </GridItem>
          </TwoColumnGrid>
          <TwoColumnGrid>
            <GridItem colSpan={1}>
              <FormInputSelectControl
                control={control}
                controlShouldRenderValue={false}
                defaultValue={null}
                errorMessage='Select a device'
                id='associateDevice'
                options={associatedDeviceOptions}
                placeholder='Select Devices'
                rules={{ required: true }}
              />
            </GridItem>
          </TwoColumnGrid>
          <TwoColumnGrid>
            <GridItem colSpan={1}>
              <Box maxH='250px' mt='3' overflowY='auto'>
                {selectedDevices?.map((d) => (
                  <Box key={d.value} maxH='250px' mb='10px'>
                    <AssociatedDevice
                      name={d?.label}
                      onClick={() => removeSelectedDeviceOption(d)}
                    />
                  </Box>
                ))}
              </Box>
            </GridItem>
          </TwoColumnGrid>
        </EditDeviceModalSection>
      )}
    </Box>
  )
}
